import { Link, useNavigate } from "react-router-dom";
import { 
  ArrowRight, 
  CheckCircle2, 
  Shield, 
  Building2,
  Users,
  FileText,
  Lock,
  Fingerprint,
  Search,
  GitBranch,
  ClipboardCheck,
  Eye,
  Briefcase,
  Palette,
  Code,
  Webhook,
  Upload,
  Scale,
  Home,
  Car,
  Landmark,
  CreditCard,
  Layers,
  Terminal,
  ChevronRight,
  Zap,
  Globe
} from "lucide-react";
import { Button } from "../components/ui/button";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/2b44956d-c622-4e48-bd96-a4350579bfd0/images/7a293a949d404e56a7ff3429a9c59bfaa57d19d06de2414ebe6b7101b67d3c87.png";
const WHITELABEL_IMG = "https://static.prod-images.emergentagent.com/jobs/2b44956d-c622-4e48-bd96-a4350579bfd0/images/2f7b1072d128321e06d297112054aa647e9321c131d8e51f721781286157f668.png";

export default function PartnersVerifyPage() {
  const navigate = useNavigate();
  const products = [
    {
      name: "Financify Check",
      tagline: "Identity Verification",
      description: "Verify government-issued IDs and match against applicant data with automated document extraction.",
      icon: Fingerprint,
    },
    {
      name: "Financify Global Screening",
      tagline: "PEP & Sanctions Screening",
      description: "Screen applicants against global sanctions lists, PEP databases, and adverse media sources.",
      icon: Search,
    },
    {
      name: "Financify Flow",
      tagline: "Onboarding Orchestration",
      description: "Route cases through configurable workflows with automatic risk scoring and decision branching.",
      icon: GitBranch,
    },
    {
      name: "Financify Review",
      tagline: "Manual Review Queue",
      description: "Manage flagged cases with a structured review queue, annotation tools, and full audit trail.",
      icon: ClipboardCheck,
    },
    {
      name: "Financify Monitor",
      tagline: "Ongoing Monitoring",
      description: "Set refresh intervals for periodic re-screening and get alerts on status changes or new risk signals.",
      icon: Eye,
    },
    {
      name: "Financify KYB",
      tagline: "Business Verification",
      description: "Verify business registrations, beneficial ownership structures, and corporate compliance status.",
      icon: Building2,
    }
  ];

  const steps = [
    { step: 1, title: "Collect", description: "Gather customer details and documents", icon: Users },
    { step: 2, title: "Upload", description: "Submit IDs and supporting files", icon: Upload },
    { step: 3, title: "Verify", description: "Run automated checks and screening", icon: Shield },
    { step: 4, title: "Route", description: "Score risk and assign case path", icon: GitBranch },
    { step: 5, title: "Decide", description: "Approve, escalate, or request more", icon: CheckCircle2 }
  ];

  const industries = [
    { name: "Commercial Banks", icon: Landmark },
    { name: "Credit Unions", icon: Users },
    { name: "Mortgage Brokers", icon: Home },
    { name: "Insurance Providers", icon: Shield },
    { name: "Auto Dealers", icon: Car },
    { name: "Real Estate Firms", icon: Building2 },
    { name: "Corporate Services", icon: Briefcase },
    { name: "Fintechs", icon: CreditCard }
  ];

  const whiteLabelFeatures = [
    { name: "Custom Branding", description: "Logo, colors, and domain under your identity", icon: Palette },
    { name: "Flexible Intake", description: "Configure what data you collect at onboarding", icon: Layers },
    { name: "Decision Rules", description: "Define your own risk thresholds and routing logic", icon: Scale },
    { name: "Document Templates", description: "Customise required documents per product type", icon: FileText }
  ];

  const apis = [
    { name: "Applicant API", description: "Create and manage applicant records", icon: Users },
    { name: "Document API", description: "Upload and verify documents securely", icon: Upload },
    { name: "Screening API", description: "Run compliance and identity checks", icon: Shield },
    { name: "Decision API", description: "Submit and retrieve case decisions", icon: CheckCircle2 },
    { name: "Webhooks", description: "Real-time event notifications", icon: Webhook }
  ];

  return (
    <div className="bg-[#0a1628] min-h-screen" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="relative py-16 md:py-28 overflow-hidden" data-testid="hero-section">
        {/* Background image + overlay */}
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-[0.12] mix-blend-screen" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628] via-[#0a1628]/80 to-[#0a1628]" />
        </div>
        {/* Radial glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#52BFB8]/[0.08] blur-[120px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-[0.2em] uppercase bg-[#52BFB8]/10 text-[#52BFB8] border border-[#52BFB8]/20 mb-6" data-testid="hero-badge">
              <Shield className="w-3.5 h-3.5" />
              Financify Verify
            </span>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.1] tracking-tight text-white mb-5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Faster onboarding.{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#52BFB8] to-[#60a5fa]">
                Smarter compliance.
              </span>
            </h1>

            <p className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              Help your institution verify customers, run KYC checks, and route decisions faster — while keeping final approval authority in-house.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                size="lg"
                className="w-full sm:w-auto bg-[#52BFB8] hover:bg-[#3ea8a1] text-[#0a1628] font-medium px-8 py-6 text-base rounded-lg shadow-[0_0_24px_rgba(82,191,184,0.35)]"
                data-testid="hero-demo-cta"
                onClick={() => navigate("/book-demo")}
              >
                Schedule a Demo
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-[#FDD552]/30 text-[#FDD552] hover:bg-[#FDD552]/10 font-medium px-8 py-6 text-base rounded-lg"
                data-testid="hero-portal-cta"
                onClick={() => navigate("/partners/portal")}
              >
                Partner Portal
              </Button>
            </div>

            {/* Trust line */}
            <p className="mt-8 text-xs text-slate-500 tracking-wide">
              Trusted by banks, credit unions, and regulated businesses across The Bahamas
            </p>
          </div>
        </div>
      </section>

      {/* ── Product Cards ────────────────────────────────── */}
      <section className="py-12 md:py-24" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">

          {/* Flagship heading */}
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-[#52BFB8] mb-3">Product Suite</span>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Lend smarter. Onboard faster. Stay compliant.
            </h2>
            <p className="text-slate-400 mt-3 max-w-2xl mx-auto text-sm md:text-base">
              One platform to power your lending, verification, and compliance workflows.
            </p>
          </div>

          {/* ── Flagship Product ── */}
          <div
            className="relative overflow-hidden rounded-3xl mb-10 lg:mb-14 border border-[#52BFB8]/25"
            style={{ background: 'linear-gradient(135deg, #0d2137 0%, #122a42 40%, #0f2d3d 100%)' }}
            data-testid="flagship-product"
          >
            {/* Glow accent */}
            <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-[#52BFB8]/[0.07] blur-[100px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-60 h-60 rounded-full bg-[#60a5fa]/[0.05] blur-[80px] pointer-events-none" />

            <div className="relative z-10 p-6 md:p-10 lg:p-12">
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-14 items-start">
                {/* Left — text */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.15em] uppercase bg-[#52BFB8]/15 text-[#52BFB8] border border-[#52BFB8]/25">
                      Flagship
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.15em] uppercase bg-[#FDD552]/10 text-[#FDD552] border border-[#FDD552]/20">
                      White-Label
                    </span>
                  </div>

                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-3 leading-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    Financify Lend<span className="text-[#52BFB8]">.</span>
                  </h3>
                  <p className="text-base md:text-lg text-slate-300 mb-5 leading-relaxed max-w-xl">
                    End-to-end white-label loan application flow with built-in KYC verification, sanctions screening, PEP checks, adverse media search, and automated risk scoring — purpose-built for regulated companies under Central Bank and Bahamian law.
                  </p>

                  {/* Loan types */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {["Mortgage", "Personal Loans", "Auto Finance"].map((t) => (
                      <span key={t} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/[0.06] text-slate-300 border border-white/[0.08]">
                        {t}
                      </span>
                    ))}
                  </div>

                  <p className="text-sm text-slate-400 mb-6 leading-relaxed">
                    Fully customizable with your company branding, logo, and colors. Integrate via API into your existing systems or use our ready-made partner portal.
                  </p>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      className="bg-[#52BFB8] hover:bg-[#3ea8a1] text-[#0a1628] font-semibold px-7 py-5 rounded-xl shadow-[0_0_20px_rgba(82,191,184,0.3)]"
                      onClick={() => navigate("/book-demo")}
                      data-testid="flagship-demo-btn"
                    >
                      Schedule a Demo
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                    <Button
                      variant="outline"
                      className="border-white/15 text-slate-300 hover:bg-white/5 px-7 py-5 rounded-xl"
                      onClick={() => navigate("/partners/lend-demo")}
                      data-testid="flagship-try-btn"
                    >
                      See How It Works
                    </Button>
                  </div>
                </div>

                {/* Right — capability grid */}
                <div className="w-full lg:w-[340px] flex-shrink-0">
                  <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-slate-500 mb-3">Built-in Capabilities</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: "KYC Verification", icon: Fingerprint },
                      { label: "Sanctions Screening", icon: Shield },
                      { label: "PEP Database", icon: Search },
                      { label: "Adverse Media", icon: Eye },
                      { label: "Risk Scoring", icon: Scale },
                      { label: "Document Checks", icon: FileText },
                    ].map(({ label, icon: Icon }) => (
                      <div key={label} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-white/[0.03] border border-white/[0.05]">
                        <div className="w-7 h-7 rounded-lg bg-[#52BFB8]/10 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-3.5 h-3.5 text-[#52BFB8]" />
                        </div>
                        <span className="text-[11px] text-slate-400 font-medium leading-tight">{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Individual Products ── */}
          <div className="text-center mb-8">
            <h3 className="text-xl sm:text-2xl font-semibold tracking-tight text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Everything you need for customer verification
            </h3>
            <p className="text-slate-500 mt-2 text-sm">Modular tools that work together or standalone.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {products.map((product, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-2xl bg-[#111d32]/50 backdrop-blur-sm border border-white/[0.08] p-5 lg:p-7 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:border-[#52BFB8]/30 hover:bg-[#111d32]/80"
                data-testid={`product-card-${product.name.toLowerCase()}`}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-[#0a1628] border border-white/10 mb-5 group-hover:border-[#52BFB8]/50 transition-colors">
                  <product.icon className="w-5 h-5 text-slate-300 group-hover:text-[#52BFB8] transition-colors" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-0.5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{product.name}</h3>
                <p className="text-xs font-medium text-[#52BFB8] mb-2 tracking-wide">{product.tagline}</p>
                <p className="text-sm text-slate-400 leading-relaxed">{product.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ─────────────────────────────────── */}
      <section className="py-12 md:py-24 bg-[#0d1e35]" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-[#52BFB8] mb-3">Workflow</span>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              From intake to decision in five steps
            </h2>
          </div>

          {/* Desktop: horizontal */}
          <div className="hidden md:block max-w-5xl mx-auto">
            <div className="relative">
              {/* Connection line */}
              <div className="absolute top-8 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-[#52BFB8]/30 to-transparent" />
              <div className="grid grid-cols-5 gap-4">
                {steps.map((s, i) => (
                  <div key={i} className="relative text-center">
                    <div className="relative z-10 w-16 h-16 mx-auto rounded-2xl bg-[#0a1628] border border-white/10 flex items-center justify-center mb-4 group">
                      <s.icon className="w-6 h-6 text-[#52BFB8]" />
                      <span className="absolute -top-2.5 -right-2.5 w-6 h-6 rounded-full bg-[#52BFB8] text-[#0a1628] text-[11px] font-bold flex items-center justify-center">{s.step}</span>
                    </div>
                    <p className="text-sm font-semibold text-white mb-1">{s.title}</p>
                    <p className="text-xs text-slate-500 leading-snug">{s.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile: vertical timeline */}
          <div className="md:hidden space-y-0">
            {steps.map((s, i) => (
              <div key={i} className="flex gap-4 items-start">
                {/* Timeline column */}
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 rounded-xl bg-[#0a1628] border border-white/10 flex items-center justify-center flex-shrink-0 relative">
                    <s.icon className="w-4.5 h-4.5 text-[#52BFB8]" />
                    <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#52BFB8] text-[#0a1628] text-[10px] font-bold flex items-center justify-center">{s.step}</span>
                  </div>
                  {i < steps.length - 1 && <div className="w-px h-8 bg-white/10 my-1" />}
                </div>
                {/* Content */}
                <div className="pb-6 pt-1.5">
                  <p className="text-sm font-semibold text-white">{s.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{s.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Who It's For ─────────────────────────────────── */}
      <section className="py-12 md:py-24" data-testid="audience-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-[#52BFB8] mb-3">Industries</span>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Built for regulated businesses
            </h2>
            <p className="text-slate-400 mt-3 max-w-xl mx-auto text-sm md:text-base">
              Trusted by institutions that need robust customer onboarding and compliance controls
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4 max-w-4xl mx-auto">
            {industries.map((ind, i) => (
              <div
                key={i}
                className="group rounded-xl bg-[#111d32]/50 border border-white/[0.08] p-4 text-center hover:border-[#52BFB8]/30 hover:bg-[#111d32]/80 transition-all duration-300"
                data-testid={`industry-${ind.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-10 h-10 mx-auto rounded-lg bg-[#0a1628] border border-white/10 flex items-center justify-center mb-2.5 group-hover:border-[#52BFB8]/50 transition-colors">
                  <ind.icon className="w-4.5 h-4.5 text-slate-400 group-hover:text-[#52BFB8] transition-colors" />
                </div>
                <p className="text-sm font-medium text-slate-300">{ind.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── White-Label Section ───────────────────────────── */}
      <section className="py-12 md:py-24 bg-[#0d1e35]" data-testid="whitelabel-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-center">
            {/* Text side */}
            <div>
              <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-[#52BFB8] mb-3">White-Label</span>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Your brand. Your portal. Our infrastructure.
              </h2>
              <p className="text-slate-400 mb-8 leading-relaxed">
                Launch a fully branded onboarding experience under your own identity — logo, colors, domain, and custom decision rules. Financify runs invisibly in the background.
              </p>

              <div className="grid sm:grid-cols-2 gap-3">
                {whiteLabelFeatures.map((f, i) => (
                  <div key={i} className="flex items-start gap-3 p-3.5 rounded-xl bg-[#0a1628]/60 border border-white/[0.06]">
                    <div className="w-9 h-9 rounded-lg bg-[#52BFB8]/10 flex items-center justify-center flex-shrink-0">
                      <f.icon className="w-4 h-4 text-[#52BFB8]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{f.name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{f.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Visual side — branded preview cards */}
            <div className="relative flex items-center justify-center">
              {/* Glow behind image */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-72 h-72 rounded-full bg-[#52BFB8]/[0.1] blur-[80px]" />
              </div>

              {/* Branded card stack preview */}
              <div className="relative w-full max-w-md">
                <img
                  src={WHITELABEL_IMG}
                  alt="White-label branded onboarding cards"
                  className="w-full h-auto relative z-10 drop-shadow-2xl"
                  data-testid="whitelabel-preview-image"
                />
                {/* Floating labels */}
                <div className="absolute top-4 -left-2 sm:-left-6 z-20 bg-[#111d32] border border-white/10 rounded-lg px-3 py-2 shadow-lg">
                  <div className="flex items-center gap-2">
                    <Palette className="w-3.5 h-3.5 text-[#52BFB8]" />
                    <span className="text-[11px] text-slate-300 font-medium">Your Brand</span>
                  </div>
                </div>
                <div className="absolute bottom-6 -right-2 sm:-right-6 z-20 bg-[#111d32] border border-white/10 rounded-lg px-3 py-2 shadow-lg">
                  <div className="flex items-center gap-2">
                    <Lock className="w-3.5 h-3.5 text-[#60a5fa]" />
                    <span className="text-[11px] text-slate-300 font-medium">Your Rules</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── API Section ──────────────────────────────────── */}
      <section className="py-12 md:py-24" data-testid="api-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-start">
            {/* Left — description + endpoints */}
            <div>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-[0.2em] uppercase text-[#60a5fa] mb-3">
                <Terminal className="w-3.5 h-3.5" />
                Developer Ready
              </span>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Connect through API or use the portal
              </h2>
              <p className="text-slate-400 mb-8 leading-relaxed">
                Integrate directly into your existing systems with RESTful endpoints, or use our intuitive web portal for manual workflows.
              </p>

              <div className="space-y-2.5">
                {apis.map((api, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-3 rounded-xl bg-[#111d32]/50 border border-white/[0.06] hover:border-[#60a5fa]/30 transition-colors group"
                    data-testid={`api-endpoint-${i}`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#60a5fa]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#60a5fa]/15 transition-colors">
                      <api.icon className="w-4 h-4 text-[#60a5fa]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">{api.name}</p>
                      <p className="text-xs text-slate-500">{api.description}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-[#60a5fa] transition-colors flex-shrink-0" />
                  </div>
                ))}
              </div>
            </div>

            {/* Right — dashboard preview mockup */}
            <div className="relative">
              <div className="rounded-2xl bg-[#0a0f1a] border border-white/[0.06] overflow-hidden shadow-2xl">
                {/* Window bar */}
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06] bg-[#0d1320]">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
                  </div>
                  <span className="text-[10px] text-slate-500 font-medium">Partner Dashboard</span>
                  <div className="w-12" />
                </div>

                {/* Dashboard content */}
                <div className="p-4 md:p-5">
                  {/* Metrics row */}
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {[
                      { label: "Applicants", value: "1,247", color: "text-white" },
                      { label: "Approved", value: "892", color: "text-[#52BFB8]" },
                      { label: "Pending", value: "64", color: "text-amber-400" },
                    ].map((m, i) => (
                      <div key={i} className="bg-[#111d32]/80 rounded-lg p-2.5 border border-white/[0.04]">
                        <p className="text-[10px] text-slate-500">{m.label}</p>
                        <p className={`text-lg font-bold ${m.color}`}>{m.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Mini table */}
                  <div className="bg-[#111d32]/50 rounded-lg border border-white/[0.04] overflow-hidden">
                    <div className="grid grid-cols-4 gap-2 px-3 py-2 border-b border-white/[0.04]">
                      {["Applicant", "Status", "Bank", "Amount"].map(h => (
                        <span key={h} className="text-[9px] text-slate-600 font-medium uppercase tracking-wider">{h}</span>
                      ))}
                    </div>
                    {[
                      { name: "M. Thompson", status: "Approved", statusColor: "bg-[#52BFB8]/15 text-[#52BFB8]", bank: "Bank A", amount: "$65,000" },
                      { name: "R. Baptiste", status: "Pending", statusColor: "bg-amber-500/15 text-amber-400", bank: "Bank B", amount: "$42,000" },
                      { name: "D. Knowles", status: "Approved", statusColor: "bg-[#52BFB8]/15 text-[#52BFB8]", bank: "Bank C", amount: "$38,500" },
                      { name: "S. Ferguson", status: "Review", statusColor: "bg-blue-500/15 text-blue-400", bank: "Bank D", amount: "$55,000" },
                    ].map((row, i) => (
                      <div key={i} className="grid grid-cols-4 gap-2 px-3 py-2 border-b border-white/[0.02] last:border-0">
                        <span className="text-[11px] text-slate-300 font-medium truncate">{row.name}</span>
                        <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full w-fit ${row.statusColor}`}>{row.status}</span>
                        <span className="text-[11px] text-slate-500 truncate">{row.bank}</span>
                        <span className="text-[11px] text-slate-300 font-medium">{row.amount}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Compliance Section ────────────────────────────── */}
      <section className="py-12 md:py-24 bg-[#0d1e35] relative overflow-hidden" data-testid="compliance-section">
        {/* Subtle background texture */}
        <div className="absolute inset-0 pointer-events-none">
          <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-[0.06] mix-blend-screen" />
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-8 relative z-10 text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#52BFB8]/10 border border-[#52BFB8]/20 flex items-center justify-center mb-6">
            <Shield className="w-7 h-7 text-[#52BFB8]" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white mb-5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Built to support strong onboarding controls
          </h2>
          <p className="text-slate-400 leading-relaxed mb-8 max-w-2xl mx-auto">
            Financify Verify helps businesses structure onboarding, verification, and screening workflows more efficiently. Final customer acceptance, escalation, and compliance decisions remain with the partner institution.
          </p>

          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {[
              "You keep final decision authority",
              "Full audit trail on every case",
              "Role-based access controls",
              "Encrypted data at rest and in transit"
            ].map((text, i) => (
              <div key={i} className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-[#0a1628]/60 border border-white/[0.08]">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#52BFB8] flex-shrink-0" />
                <span className="text-xs text-slate-300 font-medium">{text}</span>
              </div>
            ))}
          </div>

          {/* Compliance standards */}
          <div className="flex items-center justify-center gap-6 text-slate-600">
            <div className="flex flex-col items-center gap-1">
              <Lock className="w-5 h-5" />
              <span className="text-[10px] font-semibold tracking-wider uppercase">SOC 2</span>
            </div>
            <div className="w-px h-8 bg-white/[0.06]" />
            <div className="flex flex-col items-center gap-1">
              <Globe className="w-5 h-5" />
              <span className="text-[10px] font-semibold tracking-wider uppercase">ISO 27001</span>
            </div>
            <div className="w-px h-8 bg-white/[0.06]" />
            <div className="flex flex-col items-center gap-1">
              <Shield className="w-5 h-5" />
              <span className="text-[10px] font-semibold tracking-wider uppercase">GDPR</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Demo CTA ─────────────────────────────────────── */}
      <section className="py-16 md:py-28 relative overflow-hidden" data-testid="demo-cta-section">
        {/* Glow background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-[#52BFB8]/[0.08] blur-[120px]" />
        </div>

        <div className="max-w-3xl mx-auto px-4 md:px-8 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 mb-6">
            <Zap className="w-5 h-5 text-[#52BFB8]" />
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-white mb-5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            See Financify Verify in action
          </h2>
          <p className="text-slate-400 mb-10 max-w-lg mx-auto leading-relaxed">
            Schedule a personalized demo and discover how our platform can transform your onboarding process.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              size="lg"
              className="w-full sm:w-auto bg-[#52BFB8] hover:bg-[#3ea8a1] text-[#0a1628] font-semibold px-10 py-6 text-base rounded-lg shadow-[0_0_30px_rgba(82,191,184,0.4)]"
              data-testid="cta-demo-btn"
              onClick={() => navigate("/book-demo")}
            >
              Schedule a Demo
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="w-full sm:w-auto border-white/20 text-white hover:bg-white/5 font-medium px-10 py-6 text-base rounded-lg"
              data-testid="cta-partnerships-btn"
              onClick={() => navigate("/partners/portal")}
            >
              Partner Portal
            </Button>
          </div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────── */}
      <footer className="py-8 border-t border-white/[0.06]" data-testid="partners-footer">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#52BFB8]" />
              <span className="text-sm text-slate-400 font-medium">Financify Verify</span>
            </div>
            <div className="flex items-center gap-6 text-xs text-slate-600">
              <Link to="/privacy" className="hover:text-slate-300 transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-slate-300 transition-colors">Terms</Link>
              <Link to="/contact" className="hover:text-slate-300 transition-colors">Contact</Link>
            </div>
            <p className="text-xs text-slate-700">&copy; {new Date().getFullYear()} Financify Caribbean. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
